import React from 'react';

import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { TranslateFunction } from 'react-localize-redux';

import { Button } from '../../../../components/atoms/Button';
import { Typography } from '../../../../components/atoms/Typography';
import { Dialog } from '../../../../components/layouts/Dialog/Dialog';
import { MenuItemsList } from '../../../../components/organisms/MenuItemsList';
import { Currency } from '../../../../core/api/general/general.types';
import { MenuItem } from '../../../../core/api/menu/menu.types';
import {
  AddItemHookPayload,
  BasketSource,
  RemoveItemHookPayload,
} from '../../../../core/types/basket.types';
import { ClickItemPayloadType } from '../../../../core/types/menu.types';
import { Item as BasketItem } from '../../../../weborder/BasketControl';

interface Props {
  basketItems: BasketItem[];
  currency: Currency;
  languageCode: string;
  localisedTotal: string;
  menuItems: MenuItem[];
  onAddItem: (payload: AddItemHookPayload) => void;
  onCheckout: () => void;
  onClickItem: (payload: ClickItemPayloadType) => void;
  onCloseDialog: () => void;
  onRemoveItem: (payload: RemoveItemHookPayload) => void;
  translate: TranslateFunction;
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    '& .MuiDialogContent-root': {
      padding: theme.spacing(0, 0, 4, 0),
    },
  },
  title: {
    padding: theme.spacing(0, 1.5),
  },
  checkoutButton: {
    '& .buttonState': {
      display: 'flex',
      justifyContent: 'space-between',
      width: '100%',
    },
  },
}));

export function CrossSellDialog(props: Props): JSX.Element {
  const {
    basketItems,
    currency,
    languageCode,
    localisedTotal,
    menuItems,
    onAddItem,
    onCheckout,
    onClickItem,
    onCloseDialog,
    onRemoveItem,
    translate,
  } = props;
  const classes = useStyles();

  const crossSellItems = menuItems.slice(0, 5);

  const renderCheckoutButton = (): JSX.Element => (
    <Button
      className={classes.checkoutButton}
      data-fd="cross-sell-dialog-checkout-button"
      fullWidth
      onClick={onCheckout}
      type="submit"
    >
      <span>{translate('Menu_GoToCheckout')}</span>
      <span>{localisedTotal}</span>
    </Button>
  );

  return (
    <Dialog
      actions={renderCheckoutButton()}
      className={classes.root}
      dialogName="restaurant-menu-cross-sell"
      onClose={onCloseDialog}
      onTitleClick={onCloseDialog}
      open
    >
      <Typography className={classes.title} variant="h1">
        {translate('CrossSell_peopleAlsoAdded')}
      </Typography>

      <MenuItemsList
        basketItems={basketItems}
        currency={currency}
        hasBorder
        languageCode={languageCode}
        menuItems={crossSellItems}
        onAddItem={onAddItem}
        onClickItem={onClickItem}
        onRemoveItem={onRemoveItem}
        source={BasketSource.MenuCrossSellDialog}
        translate={translate}
        variant="compact"
      />
    </Dialog>
  );
}
